/**
 * TeamsSection: the signed-in user's teams. Switch between them, or delete
 * one you own. User-scoped (see sections.ts): under staff impersonation
 * this still lists the staff member's own teams.
 *
 * Team creation is deliberately absent: staff provision teams from the
 * Employees page (POST /api/internal/teams), which yields the clean slug.
 */

import { useState } from 'react'
import { Check, Trash2 } from 'lucide-react'

import { getApiErrorDetail } from '@/api/client'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { TeamDeleteDialog } from '@/components/shared/TeamDeleteDialog'
import { EYEBROW } from '@/components/ui/eyebrow'
import { useAuth } from '@/contexts/AuthContext'
import { useTeam } from '@/contexts/TeamContext'
import { useDeleteTeam, useTeamDeletePreview } from '@/hooks/useTeamSettings'

import { RowAction } from './RowAction'
import { SettingsList, SettingsListRow } from './SettingsList'
import { SettingsPageHeader } from './SettingsPageHeader'
import { SettingsSection } from './SettingsSection'
import { afterTeamDeleted } from './teamDelete'

interface TeamRef {
  id: string
  name: string
}

export function TeamsSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { teams, activeTeamId, switchTeam, refetch, vanityOrg, impersonatingTeamId } = useTeam()
  const { signinSilent } = useAuth()
  // Vanity hosts are locked to their org (TRA-829): list only that team,
  // and no deletes — the other teams belong to the canonical host.
  const visibleTeams = vanityOrg === null ? teams : teams.filter((t) => t.id === activeTeamId)
  // Deleting is keyed by the caller (the API checks ownership by user, and
  // never reads the team header), so it works under impersonation. It is
  // hidden there anyway: every other section offers no writes while
  // impersonating, and this one should read the same.
  const canDelete = vanityOrg === null && teams.length > 1 && impersonatingTeamId === null
  const deleteMutation = useDeleteTeam()
  const [teamToDelete, setTeamToDelete] = useState<TeamRef | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const preview = useTeamDeletePreview(teamToDelete?.id ?? null)

  const handleDelete = (target: TeamRef, confirm: string): void => {
    setDeleteError(null)
    deleteMutation.mutate(
      { teamId: target.id, params: { confirm } },
      {
        onSuccess: async () => {
          // Deleting the active team moves you to another one; switchTeam
          // reloads the page, so nothing below it runs.
          const outcome = await afterTeamDeleted({
            deletedTeamId: target.id,
            activeTeamId,
            teams,
            refreshToken: signinSilent,
            switchTeam,
            refetchTeams: refetch,
          })
          if (outcome === 'refetched') setTeamToDelete(null)
        },
        onError: (error) => {
          setDeleteError(getApiErrorDetail(error) ?? 'Failed to delete team. Please try again.')
        },
      },
    )
  }

  return (
    <div>
      <SettingsPageHeader section={section} />
      <SettingsSection
        title="Your teams"
        description={
          canDelete
            ? 'Switching reloads the app scoped to that team. Deleting a team removes everything it owns.'
            : 'Switching reloads the app scoped to that team.'
        }
      >
        {visibleTeams.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No teams found. Contact TrainLoop to get your team set up.
          </p>
        ) : (
          <SettingsList>
            {visibleTeams.map((team) => (
              <TeamRow
                key={team.id}
                name={team.name}
                slug={team.slug}
                isActive={team.id === activeTeamId}
                onSwitch={() => {
                  switchTeam(team.id)
                }}
                onDelete={
                  canDelete
                    ? () => {
                        setDeleteError(null)
                        setTeamToDelete({ id: team.id, name: team.name })
                      }
                    : null
                }
              />
            ))}
          </SettingsList>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Teams are provisioned by TrainLoop.{' '}
          <a
            href="mailto:support@trainloop.ai"
            className="text-foreground underline underline-offset-4 hover:text-primary"
          >
            Contact support
          </a>{' '}
          to add one.
        </p>
      </SettingsSection>

      <TeamDeleteDialog
        open={teamToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setTeamToDelete(null)
        }}
        teamName={teamToDelete?.name ?? ''}
        preview={preview}
        isPending={deleteMutation.isPending}
        error={deleteError}
        onConfirm={(confirm) => {
          if (teamToDelete) handleDelete(teamToDelete, confirm)
        }}
      />
    </div>
  )
}

function TeamRow({
  name,
  slug,
  isActive,
  onSwitch,
  onDelete,
}: {
  name: string
  slug: string
  isActive: boolean
  onSwitch: () => void
  onDelete: (() => void) | null
}): React.JSX.Element {
  return (
    <SettingsListRow className="min-h-12 py-0">
      <span className="flex w-4 shrink-0 text-foreground" aria-hidden="true">
        {isActive && <Check className="h-4 w-4" />}
      </span>
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-3 py-2 text-left"
        aria-current={isActive ? 'true' : undefined}
        onClick={() => {
          if (!isActive) onSwitch()
        }}
      >
        <span className="text-sm text-foreground">{name}</span>
        <span className="font-mono text-xs text-muted-foreground">{slug}</span>
      </button>
      {isActive && <span className={EYEBROW}>Active</span>}
      {onDelete !== null && (
        <RowAction label={`Delete ${name}`} icon={Trash2} destructive onClick={onDelete} />
      )}
    </SettingsListRow>
  )
}
